import { Button, useDisclosure } from "@heroui/react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { TableBuilder } from "@/app/components/tables/tableBuilder";
import { FaPencil, FaPlus } from "react-icons/fa6";
import { api } from "@/api";
import { SigurIntegration } from "@/api/sigur";
import useErrorHandling from "@/hooks/useErrorHandling";
import { ColumnType } from "@/app/components/tables/tableTypes";
import { CreateSigurIntegrationModal } from "@/app/components/modals/sigur/createSigurIntegrationModal";
import { EditSigurIntegrationModal } from "@/app/components/modals/sigur/editSigurIntegrationModal";
import {
  businessProfileLinks,
  Links,
} from "@/app/components/navigation/links/Links";

export function SigurIntegrationsPage() {
  const [sigurIntegrations, setSigurIntegrations] = useState<
    SigurIntegration[] | null
  >(null);
  const [sigurIntegration, setSigurIntegration] =
    useState<SigurIntegration | null>(null);

  const handleError = useErrorHandling();

  const createSigurIntegrationModalDisclosure = useDisclosure();
  const editSigurIntegrationModalDisclosure = useDisclosure();

  const editSigurIntegration = useCallback(
    (integration: SigurIntegration) => {
      setSigurIntegration(integration);
      editSigurIntegrationModalDisclosure.onOpen();
    },
    [editSigurIntegrationModalDisclosure],
  );

  const loadSigurIntegrations = useCallback(() => {
    api.sigur
      .list()
      .then((response) => {
        setSigurIntegrations(response.data);
      })
      .catch((err) => {
        console.log(err);
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
      });
  }, [handleError]);

  useEffect(() => {
    loadSigurIntegrations();
  }, [loadSigurIntegrations]);

  useEffect(() => {
    if (
      !createSigurIntegrationModalDisclosure.isOpen &&
      !editSigurIntegrationModalDisclosure.isOpen
    ) {
      loadSigurIntegrations();
    }
  }, [
    createSigurIntegrationModalDisclosure.isOpen,
    editSigurIntegrationModalDisclosure.isOpen,
    loadSigurIntegrations,
  ]);

  return (
    <>
      <CreateSigurIntegrationModal
        disclosure={createSigurIntegrationModalDisclosure}
      />
      <EditSigurIntegrationModal
        disclosure={editSigurIntegrationModalDisclosure}
        sigurIntegration={sigurIntegration}
      />

      <div className="flex flex-row gap-4 w-full">
        <Links links={businessProfileLinks("/dashboard/service/sigur")} />
        <div className="flex-grow bg-white dark:bg-zinc-900 rounded-xl border-2 border-foreground-200 dark:border-foreground-100 p-4">
          <div className="flex flex-row justify-between w-full items-center mb-4">
            <div className="flex flex-row gap-4 items-center">
              <span className="font-medium">Интеграции с Сигуром</span>
            </div>
            <div className="flex flex-row gap-4 items-center">
              <Button
                startContent={<FaPlus />}
                color="primary"
                size="sm"
                variant="solid"
                className="max-w-fit"
                onPress={() => createSigurIntegrationModalDisclosure.onOpen()}>
                Создать
              </Button>
            </div>
          </div>
          <div className="flex flex-col gap-2 mb-2">
            <TableBuilder
              displayOptions={false}
              removeWrapper={true}
              columns={[
                {
                  label: "Название",
                  key: "name",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  label: "Локация",
                  key: "location_id",
                  type: ColumnType.Location,
                  sortable: true,
                },
                {
                  label: "Система замков",
                  key: "credentials_id",
                  type: ColumnType.LockSystem,
                  sortable: true,
                },
                {
                  label: "Токен",
                  key: "token",
                  type: ColumnType.String,
                  sortable: true,
                },
                {
                  key: "actions",
                  label: "Действия",
                  type: ColumnType.Actions,
                  actions: [
                    {
                      icon: <FaPencil />,
                      onClick: (_value, row) => {
                        editSigurIntegration(row);
                      },
                    },
                  ],
                },
              ]}
              data={sigurIntegrations || []}
              rowsPerPage={10}
              defaultSortDescriptor={{
                column: "name",
                direction: "ascending",
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
